import React, { useContext, useSyncExternalStore } from 'react';
import classNames from 'classnames';
import { CloseOutlined } from '@ant-design/icons';
import { FilterBarInternalContext } from '../state/FilterBarContext';
import { shallowEqualValue } from '../state/init-sequencer';
import type {
  DateRangeValue,
  FilterBarController,
  FilterBarLabels,
  FilterItemConfig,
  FilterValues,
  NumericRangeValue,
} from '../types';
import type { Dayjs } from 'dayjs';
import { collapseSelection } from './collapse';

import cn from './styles.module.scss';

export interface FilterSummaryProps<T extends FilterValues = FilterValues> {
  controller?: FilterBarController<T>;
  className?: string;
  /** Hide the "Clear all" affordance. Default: false (shown). */
  hideClearAll?: boolean;
  /**
   * Fired synchronously when the built-in Clear All affordance is clicked,
   * BEFORE `controller.clearAll()` runs — same contract as
   * `FilterBarProps.onClearAll`. Fires even when the operation nets to zero
   * value changes (the follow-up `onChange` is then silent).
   */
  onClearAll?: () => void;
  /**
   * Prefix every chip with its item's `label` ("Pay Market: Belgium").
   * Default: false — chips show the bare value label ("Belgium"). Per-item
   * `summaryPrefix` overrides this in either direction.
   */
  showItemLabels?: boolean;
}

/**
 * Chip-row summary component.
 *
 * Plan Section "Optional `FilterSummary` chip row":
 *   - Iterates `items` config + `values`.
 *   - For each non-empty field, renders one or more chips (multi-select → N chips).
 *   - Each chip has an X that calls `controller.set(name, ...)` to remove that value.
 *   - "Clear All" calls `controller.clearAll()`.
 *   - Omitted entirely if `itemConfig.excludeFromSummary === true`, or while the
 *     value equals one of `excludeFromSummaryValues`.
 *
 * Chip labels are bare value labels ("Belgium"); `showItemLabels` /
 * per-item `summaryPrefix` add "Label: " prefixes. A SELECT_MULTI group whose
 * enabled leaves are all selected collapses to one chip (see collapse.ts).
 *
 * Subscribes to the controller's `values` slot — re-renders only when values
 * actually change, not on phase/visibility transitions.
 */
export function FilterSummary<T extends FilterValues = FilterValues>(props: FilterSummaryProps<T>) {
  // Read from internal context (when nested inside FilterBar); fallback to prop controller.
  const ctx = useContext(FilterBarInternalContext);
  const controller = props.controller
    ?? (ctx?.store as unknown as FilterBarController<T> | undefined);
  if (!controller) {
    throw new Error('[FilterSummary] must receive a controller prop or be rendered inside <FilterBar>.');
  }
  return (
    <FilterSummaryInner
      {...props}
      controller={controller}
      labels={ctx?.labels}
      contextItemsByName={ctx?.itemsByName}
    />
  );
}

function FilterSummaryInner<T extends FilterValues>(props: FilterSummaryProps<T> & {
  controller: FilterBarController<T>;
  labels?: Required<FilterBarLabels>;
  contextItemsByName?: Record<string, FilterItemConfig>;
}) {
  const { controller, className, hideClearAll, onClearAll, showItemLabels, labels, contextItemsByName } = props;
  const values = useSyncExternalStore(
    controller.subscribe,
    () => controller.getState().values,
    () => controller.getState().values,
  );
  // Prefer context's items map; fall back to the controller's freshest configs
  // (standalone usage).
  const itemsByName = contextItemsByName ?? buildItemsByName(controller.getItems());

  const chips: React.ReactNode[] = [];
  for (const name of Object.keys(values)) {
    const config = itemsByName[name];
    if (!config || config.excludeFromSummary) continue;
    const value = (values as FilterValues)[name];
    // Value-conditional exclusion — sentinel values ("ALL", …) render no chip.
    if (config.excludeFromSummaryValues?.some((v) => shallowEqualValue(v, value))) continue;
    const fieldChips = renderChips(config, value, controller, name, showItemLabels);
    chips.push(...fieldChips);
  }

  if (chips.length === 0) return null;

  return (
    <div className={classNames(cn.summary, className)} data-testid="filter-summary">
      {labels && <span className={cn.summaryLabel}>{labels.filtersLabel}</span>}
      {chips}
      {!hideClearAll && (
        <button
          type="button"
          className={cn.clearAllButton}
          onClick={() => {
            // Page hook first, then the operation — see FilterBarProps.onClearAll.
            onClearAll?.();
            controller.clearAll();
          }}
          data-testid="filter-summary-clear-all"
        >
          {labels?.clearAll ?? 'Clear All'}
        </button>
      )}
    </div>
  );
}

/**
 * Resolves the chip's text: bare value label by default, "Prefix: value" when
 * the summary's `showItemLabels` or the item's `summaryPrefix` asks for it
 * (item wins, in either direction). Bare chips pass ReactNode labels through
 * untouched; prefixed chips flatten to a string.
 */
function chipText(
  config: FilterItemConfig,
  showItemLabels: boolean | undefined,
  valueText: React.ReactNode,
): React.ReactNode {
  const prefix = config.summaryPrefix === undefined
    ? (showItemLabels ? config.label : null)
    : config.summaryPrefix === false
      ? null
      : config.summaryPrefix === true
        ? config.label
        : config.summaryPrefix;
  return prefix == null ? valueText : `${prefix}: ${valueText}`;
}

function renderChips<T extends FilterValues>(
  config: FilterItemConfig,
  value: unknown,
  controller: FilterBarController<T>,
  name: string,
  showItemLabels: boolean | undefined,
): React.ReactNode[] {
  if (isEmpty(value)) return [];

  if (config.type === 'SELECT_MULTI') {
    const arr = value as Array<string | number>;
    const removeCovered = (covered: Array<string | number>) => {
      const coveredSet = new Set<unknown>(covered);
      // Read fresh at click time — the chip may outlive several renders.
      const current = (controller.getValues() as FilterValues)[name];
      controller.set(name, (Array.isArray(current) ? current : []).filter((x) => !coveredSet.has(x)));
    };
    const { chips: collapsed, residual } = collapseSelection(controller.getOptions(name), arr);
    return [
      ...collapsed.map(({ option, covered }) => (
        <ChipButton
          key={`${name}-${option.value ?? `group-${String(option.label)}`}`}
          name={name}
          value={option.value}
          label={chipText(config, showItemLabels, option.label)}
          onRemove={() => removeCovered(covered)}
        />
      )),
      ...residual.map((v) => (
        <ChipButton
          key={`${name}-${v}`}
          name={name}
          value={v}
          label={chipText(config, showItemLabels, controller.findOption(name, v)?.label ?? String(v))}
          onRemove={() => removeCovered([v])}
        />
      )),
    ];
  }

  if (config.type === 'SELECT') {
    const opt = controller.findOption(name, value);
    return [
      <ChipButton
        key={name}
        name={name}
        value={value as string | number}
        label={chipText(config, showItemLabels, opt?.label ?? String(value))}
        onRemove={() => controller.set(name, null)}
      />,
    ];
  }

  if (config.type === 'TEXT_SEARCH') {
    return [
      <ChipButton
        key={name}
        name={name}
        value={value as string}
        label={chipText(config, showItemLabels, `"${value}"`)}
        onRemove={() => controller.set(name, '')}
      />,
    ];
  }

  if (config.type === 'SEGMENT_TOGGLE') {
    // Reads from the loaded options (dataSource — static or async) via the
    // controller, uniform with SELECT.
    const opt = controller.findOption(name, value);
    return [
      <ChipButton
        key={name}
        name={name}
        value={value as string | number}
        label={chipText(config, showItemLabels, opt?.label ?? String(value))}
        removable={false}
      />,
    ];
  }

  if (config.type === 'DATE_RANGE') {
    const pair = value as DateRangeValue;
    if (!pair) return [];
    const [a, b] = pair;
    const fmt = config.format ?? 'YYYY-MM-DD';
    const aStr = a ? (a as Dayjs).format(fmt) : '…';
    const bStr = b ? (b as Dayjs).format(fmt) : '…';
    return [
      <ChipButton
        key={name}
        name={name}
        label={chipText(config, showItemLabels, `${aStr} – ${bStr}`)}
        onRemove={() => controller.set(name, null)}
      />,
    ];
  }

  if (config.type === 'NUMERIC_RANGE') {
    const pair = value as NumericRangeValue;
    if (!pair) return [];
    const [a, b] = pair;
    const fmt = (n: number | null) => {
      if (n === null) return '…';
      if (config.format === 'currency') return `${config.currency ?? '$'}${n}`;
      if (config.format === 'percent') return `${n}%`;
      return String(n);
    };
    return [
      <ChipButton
        key={name}
        name={name}
        label={chipText(config, showItemLabels, `${fmt(a)} – ${fmt(b)}`)}
        onRemove={() => controller.set(name, null)}
      />,
    ];
  }

  return [];
}

interface ChipButtonProps {
  name: string;
  label: React.ReactNode;
  /**
   * Scalar value this chip represents. Exposed as `data-value` on the chip and
   * its remove button: the testid is stable per FIELD (`filter-summary-chip-
   * <name>`), so multi-value fields render duplicate testids — `data-value` is
   * the disambiguator tests select on. Omitted for composite-value chips
   * (ranges), whose field renders a single chip.
   */
  value?: string | number;
  onRemove?: () => void;
  removable?: boolean;
}

const ChipButton: React.FC<ChipButtonProps> = ({ name, label, value, onRemove, removable = true }) => (
  <span
    className={cn.summaryChip}
    data-testid={`filter-summary-chip-${name}`}
    data-value={value !== undefined ? String(value) : undefined}
  >
    {label}
    {removable && onRemove && (
      <button
        type="button"
        aria-label="Remove filter chip"
        onClick={onRemove}
        className={cn.summaryChipRemove}
        data-testid={`filter-summary-chip-remove-${name}`}
        data-value={value !== undefined ? String(value) : undefined}
      >
        <CloseOutlined />
      </button>
    )}
  </span>
);

function buildItemsByName(items: FilterItemConfig[]): Record<string, FilterItemConfig> {
  const out: Record<string, FilterItemConfig> = {};
  for (const item of items) out[item.name] = item;
  return out;
}

function isEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return true;
  if (Array.isArray(v) && v.length === 0) return true;
  if (typeof v === 'string' && v === '') return true;
  return false;
}
